import { isLeft, isRight } from 'fp-ts/Either';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { TRPC } from '../../../contexts/TRPC';
import { captureAndReportError } from '../../../utils/errorReporting';
import type { RejectionReasonOption } from '../../../hooks/useRejectionReasons';
import type {
  HubspotTicketDetailData,
  HubspotTicketStageOptionsResponse,
} from '../types';
import { showTicketStatusUpdateToast } from './showTicketStatusUpdateToast';
import type { ModeratorCapabilities } from './useModeratorCapabilities';

export const MIN_REJECTION_NOTE_LENGTH = 10;

const formatRejectionReasons = (
  reasonOptions: RejectionReasonOption[],
  selectedCodes: string[],
) =>
  selectedCodes
    .map((code) => {
      const reason = reasonOptions.find((option) => option.code === code);
      return reason ? `${reason.label} (${reason.code})` : code;
    })
    .join(', ');

interface UseModeratorActionsParams {
  ticketId: string;
  trpc: TRPC | null;
  viewerUserId: string;
  data: HubspotTicketDetailData | null;
  statusOptions: HubspotTicketStageOptionsResponse['options'];
  rejectionReasonOptions: RejectionReasonOption[];
  capabilities: ModeratorCapabilities | null;
  rejectionFailureAssigneeUserId: string | null;
  refetchTicket: () => Promise<void>;
  onTicketUpdated?: () => Promise<void>;
}

export interface UseModeratorActionsResult {
  isApproving: boolean;
  isRejecting: boolean;
  rejectionNote: string;
  rejectionReasons: string[];
  isRejectSubmitting: boolean;
  isRejectSubmitDisabled: boolean;
  isSelfAssigning: boolean;
  isAssignedToSelf: boolean;
  moderatorError: string | null;
  approvedStageFound: boolean;
  canApprove: boolean;
  canRejectDirectly: boolean;
  canReassignTickets: boolean;
  setRejectionNote: (value: string) => void;
  toggleRejectionReason: (code: string) => void;
  handleApprove: () => Promise<void>;
  handleStartReject: () => void;
  handleCancelReject: () => void;
  handleSubmitReject: () => Promise<void>;
  handleSelfAssign: () => Promise<void>;
}

export function useModeratorActions({
  ticketId,
  trpc,
  viewerUserId,
  data,
  statusOptions,
  rejectionReasonOptions,
  capabilities,
  rejectionFailureAssigneeUserId,
  refetchTicket,
  onTicketUpdated,
}: UseModeratorActionsParams): UseModeratorActionsResult {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectionNote, setRejectionNote] = useState('');
  const [rejectionReasons, setRejectionReasons] = useState<string[]>([]);
  const [isRejectSubmitting, setIsRejectSubmitting] = useState(false);
  const [isSelfAssigning, setIsSelfAssigning] = useState(false);
  const [moderatorError, setModeratorError] = useState<string | null>(null);

  const savedRejectionReasonCodesKey = (data?.rejectionReasonCodes ?? []).join(
    ',',
  );
  useEffect(() => {
    setRejectionReasons(
      savedRejectionReasonCodesKey
        ? savedRejectionReasonCodesKey.split(',')
        : [],
    );
  }, [savedRejectionReasonCodesKey, ticketId]);

  const canApprove = capabilities?.canApprove ?? true;
  const canRejectDirectly = capabilities?.canRejectDirectly ?? false;
  const canReassignTickets = capabilities?.canReassignTickets ?? false;

  const approvedStage = statusOptions.find(
    (opt) => opt.label.toLowerCase() === 'approved',
  );
  const rejectedStage =
    statusOptions.find(
      (opt) => opt.isClosed && opt.label.toLowerCase().includes('reject'),
    ) ??
    statusOptions.find(
      (opt) => opt.isClosed && opt.label.toLowerCase() !== 'approved',
    );
  const isAssignedToSelf = data?.assignedUser?.id === viewerUserId;

  const handleApprove = async () => {
    if (!trpc?.client) {
      setModeratorError('TRPC not initialized');
      return;
    }
    if (!approvedStage) {
      setModeratorError('Approved stage not found in pipeline');
      return;
    }

    setIsApproving(true);
    setModeratorError(null);

    try {
      const result = await trpc.client.updateHubspotTicketStatus.mutate({
        ticketId,
        stageId: approvedStage.value,
        closedCategory: undefined,
        closedLabel: undefined,
        reviewerNote: undefined,
      });

      if (isRight(result)) {
        await refetchTicket();
        if (onTicketUpdated) await onTicketUpdated();
        showTicketStatusUpdateToast({
          baseMessage: 'Ticket approved',
          approvalEmail: result.right.approvalEmail,
          rejectionEmail: result.right.rejectionEmail,
        });
      } else if (isLeft(result)) {
        const msg = result.left.message || 'Failed to approve ticket';
        setModeratorError(msg);
        toast.error(msg);
      }
    } catch (err) {
      captureAndReportError('Error approving ticket:', err);
      setModeratorError('Failed to approve ticket');
      toast.error('Failed to approve ticket');
    } finally {
      setIsApproving(false);
    }
  };

  const handleStartReject = () => {
    setIsRejecting(true);
    setModeratorError(null);
  };

  const handleCancelReject = () => {
    setIsRejecting(false);
    setRejectionNote('');
    setRejectionReasons([]);
    setModeratorError(null);
  };

  const toggleRejectionReason = (code: string) => {
    setRejectionReasons((currentCodes) =>
      currentCodes.includes(code)
        ? currentCodes.filter((currentCode) => currentCode !== code)
        : [...currentCodes, code],
    );
  };

  const validateRejectionInput = (): string | null => {
    if (rejectionReasons.length === 0) {
      return 'Select at least one rejection reason';
    }
    if (rejectionNote.trim().length < MIN_REJECTION_NOTE_LENGTH) {
      return `Rejection note must be at least ${MIN_REJECTION_NOTE_LENGTH} characters`;
    }
    return null;
  };

  const handleSubmitRejectDirect = async () => {
    if (!trpc?.client) {
      setModeratorError('TRPC not initialized');
      return;
    }

    const validationError = validateRejectionInput();
    if (validationError) {
      setModeratorError(validationError);
      return;
    }

    if (!rejectedStage) {
      setModeratorError('Rejected stage not found in pipeline');
      return;
    }

    setIsRejectSubmitting(true);
    setModeratorError(null);

    const trimmed = rejectionNote.trim();

    try {
      const result = await trpc.client.updateHubspotTicketStatus.mutate({
        ticketId,
        stageId: rejectedStage.value,
        closedCategory: rejectedStage.label,
        closedLabel: rejectedStage.label,
        reviewerNote: trimmed,
        rejectionReasonCodes: rejectionReasons,
      });

      if (isRight(result)) {
        await refetchTicket();
        if (onTicketUpdated) await onTicketUpdated();
        toast.success('Ticket rejected');
        setIsRejecting(false);
        setRejectionNote('');
        setRejectionReasons([]);
      } else if (isLeft(result)) {
        const msg = result.left.message || 'Failed to reject ticket';
        setModeratorError(msg);
        toast.error(msg);
      }
    } catch (err) {
      captureAndReportError('Error rejecting ticket:', err);
      setModeratorError('Failed to reject ticket');
      toast.error('Failed to reject ticket');
    } finally {
      setIsRejectSubmitting(false);
    }
  };

  const handleSubmitRejectSoftRedirect = async () => {
    if (!trpc?.client) {
      setModeratorError('TRPC not initialized');
      return;
    }

    const validationError = validateRejectionInput();
    if (validationError) {
      setModeratorError(validationError);
      return;
    }

    if (!rejectionFailureAssigneeUserId) {
      setModeratorError('Review rejection route is not configured');
      toast.error('Review rejection route is not configured');
      return;
    }

    setIsRejectSubmitting(true);
    setModeratorError(null);

    const trimmed = rejectionNote.trim();
    const fail = (message: string) => {
      setModeratorError(message);
      toast.error(message);
    };

    try {
      const result = await trpc.client.softRejectHubspotTicket.mutate({
        ticketId,
        rejectionReasonCodes: rejectionReasons,
        noteBody: `Rejection reasons: ${formatRejectionReasons(rejectionReasonOptions, rejectionReasons)}\n\n${trimmed}`,
      });
      if (isLeft(result)) {
        fail(result.left.message || 'Failed to reject ticket');
        return;
      }

      await refetchTicket();
      if (onTicketUpdated) await onTicketUpdated();
      toast.success('Ticket rejected and reassigned');
      setIsRejecting(false);
      setRejectionNote('');
      setRejectionReasons([]);
    } catch (err) {
      captureAndReportError('Error rejecting ticket:', err);
      setModeratorError('Failed to reject ticket');
      toast.error('Failed to reject ticket');
    } finally {
      setIsRejectSubmitting(false);
    }
  };

  const handleSubmitReject = canRejectDirectly
    ? handleSubmitRejectDirect
    : handleSubmitRejectSoftRedirect;

  // Single source of truth for reject-submit validation (≥1 reason + note ≥
  // MIN_REJECTION_NOTE_LENGTH), consumed by the reject dialog so the rule is not
  // duplicated in the presentational component.
  const isRejectSubmitDisabled =
    isRejectSubmitting ||
    rejectionReasons.length === 0 ||
    rejectionNote.trim().length < MIN_REJECTION_NOTE_LENGTH;

  const handleSelfAssign = async () => {
    if (!trpc?.client) return;

    setIsSelfAssigning(true);
    setModeratorError(null);

    try {
      const result = await trpc.client.assignHubspotTicket.mutate({
        ticketId,
        assignedUserId: isAssignedToSelf ? null : viewerUserId,
      });

      if (isRight(result)) {
        await refetchTicket();
        if (onTicketUpdated) await onTicketUpdated();
        toast.success(
          isAssignedToSelf ? 'Ticket unassigned' : 'Ticket assigned to you',
        );
      } else if (isLeft(result)) {
        const msg = result.left.message || 'Failed to update assignment';
        setModeratorError(msg);
        toast.error(msg);
      }
    } catch (err) {
      captureAndReportError('Error updating ticket assignment:', err);
      setModeratorError('Failed to update assignment');
      toast.error('Failed to update assignment');
    } finally {
      setIsSelfAssigning(false);
    }
  };

  return {
    isApproving,
    isRejecting,
    rejectionNote,
    rejectionReasons,
    isRejectSubmitting,
    isRejectSubmitDisabled,
    isSelfAssigning,
    isAssignedToSelf,
    moderatorError,
    approvedStageFound: !!approvedStage,
    canApprove,
    canRejectDirectly,
    canReassignTickets,
    setRejectionNote,
    toggleRejectionReason,
    handleApprove,
    handleStartReject,
    handleCancelReject,
    handleSubmitReject,
    handleSelfAssign,
  };
}
